import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Check, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const AboutPage = () => {
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const stagger = {
    visible: { transition: { staggerChildren: 0.1 } }
  };

  const values = [
    {
      title: 'Efficacité avant tout',
      description: 'Nous ne commercialisons que des produits qui fonctionnent vraiment. Chaque article est testé en conditions réelles avant d\'être proposé à la vente.'
    },
    {
      title: 'Simplicité',
      description: 'Nos produits sont conçus pour être simples à utiliser. Pas de modes d\'emploi compliqués, pas de fonctionnalités superflues.'
    },
    {
      title: 'Qualité premium',
      description: 'Nous privilégions la qualité à la quantité. Chaque produit est sélectionné pour sa durabilité et ses performances.'
    },
    {
      title: 'Prix juste',
      description: 'Nous proposons des produits de qualité à des prix accessibles, sans intermédiaires inutiles.'
    }
  ];

  return (
    <div data-testid="about-page" className="min-h-screen bg-white">
      {/* Header */}
      <section className="py-16 lg:py-24 bg-ghrb-gray">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="max-w-3xl"
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            <p className="font-outfit text-xs uppercase tracking-[0.2em] text-ghrb-orange mb-4">
              À propos
            </p>
            <h1 className="font-outfit text-4xl lg:text-5xl font-bold tracking-tight text-ghrb-black mb-6">
              L'efficacité, sans compromis.
            </h1>
            <p className="font-jakarta text-lg text-ghrb-gray-dark leading-relaxed">
              GHRB est né d'une conviction simple : les meilleurs produits sont ceux qui fonctionnent vraiment, 
              sans artifice ni marketing excessif. Notre mission est de sélectionner et proposer des produits 
              qui font réellement la différence dans votre quotidien.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <div className="aspect-[4/3] rounded-3xl overflow-hidden bg-ghrb-gray">
                <img
                  src="https://images.pexels.com/photos/279719/pexels-photo-279719.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Modern living space"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={stagger}
            >
              <motion.p variants={fadeInUp} className="font-outfit text-xs uppercase tracking-[0.2em] text-ghrb-orange mb-4">
                Notre mission
              </motion.p>
              <motion.h2 variants={fadeInUp} className="font-outfit text-3xl lg:text-4xl font-bold tracking-tight text-ghrb-black mb-6">
                Simplifier votre quotidien
              </motion.h2>
              <motion.p variants={fadeInUp} className="font-jakarta text-lg text-ghrb-gray-dark leading-relaxed mb-6">
                Dans un monde saturé de produits gadgets et de promesses marketing, nous avons choisi 
                une approche différente. Chaque produit GHRB répond à un besoin réel et offre une 
                solution concrète, testée et approuvée.
              </motion.p>
              <motion.p variants={fadeInUp} className="font-jakarta text-lg text-ghrb-gray-dark leading-relaxed">
                Nous ne cherchons pas à vendre le plus possible, mais à proposer les meilleurs produits 
                dans chaque catégorie que nous couvrons. C'est cette exigence qui fait la différence GHRB.
              </motion.p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 lg:py-32 bg-ghrb-gray">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <p className="font-outfit text-xs uppercase tracking-[0.2em] text-ghrb-orange mb-4">
              Nos valeurs
            </p>
            <h2 className="font-outfit text-3xl lg:text-4xl font-bold tracking-tight text-ghrb-black">
              Ce qui nous guide
            </h2>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            {values.map((value, index) => (
              <motion.div 
                key={index}
                variants={fadeInUp}
                className="bg-white p-8 lg:p-10 rounded-2xl"
                data-testid={`value-card-${index}`}
              >
                <div className="w-12 h-12 bg-ghrb-orange/10 rounded-xl flex items-center justify-center mb-6">
                  <Check className="w-6 h-6 text-ghrb-orange" />
                </div>
                <h3 className="font-outfit text-xl font-semibold text-ghrb-black mb-3">
                  {value.title}
                </h3>
                <p className="font-jakarta text-ghrb-gray-dark leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-20 lg:py-32 bg-ghrb-black text-white relative grain-overlay">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={stagger}
            >
              <motion.p variants={fadeInUp} className="font-outfit text-xs uppercase tracking-[0.2em] text-ghrb-orange mb-4">
                Notre philosophie
              </motion.p>
              <motion.h2 variants={fadeInUp} className="font-outfit text-3xl lg:text-4xl font-bold tracking-tight mb-6">
                Sélection rigoureuse
              </motion.h2>
              <motion.p variants={fadeInUp} className="font-jakarta text-lg text-white/70 leading-relaxed mb-6">
                Avant d'ajouter un produit à notre catalogue, nous passons par un processus de sélection 
                strict. Nous testons personnellement chaque article, nous vérifions sa qualité de fabrication, 
                et nous nous assurons qu'il répond vraiment au problème qu'il prétend résoudre.
              </motion.p>
              <motion.p variants={fadeInUp} className="font-jakarta text-lg text-white/70 leading-relaxed">
                C'est pourquoi notre catalogue reste volontairement limité : nous préférons proposer 
                peu de produits excellents plutôt qu'un large choix de qualité variable.
              </motion.p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <div className="aspect-square rounded-3xl overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1650219680750-6218ac8efbb3?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Quality inspection"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-32 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.h2 variants={fadeInUp} className="font-outfit text-3xl lg:text-4xl font-bold tracking-tight text-ghrb-black mb-6">
              Découvrez nos produits
            </motion.h2>
            <motion.p variants={fadeInUp} className="font-jakarta text-lg text-ghrb-gray-dark mb-10">
              Rejoignez des milliers de clients satisfaits et découvrez des produits qui font vraiment la différence.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Link to="/produits">
                <Button 
                  size="lg"
                  className="bg-ghrb-orange hover:bg-ghrb-orange-hover text-white font-jakarta font-semibold px-10 py-6 rounded-full transition-transform hover:scale-105"
                  data-testid="about-cta"
                >
                  Voir les produits
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
